import React, { useState, useEffect } from 'react';
import { MaskedInput, createDefaultMaskGenerator } from 'react-hook-mask';
import { INPUT_AUTOFILL_PROPS } from '../../../../components/InputField/autofillProps';
import { useTranslation } from '../../../../store/context/TranslationContext';
import PreviousValue from '../../../../components/InputField/PreviousValue';
import { DEFAULT_NUMERIC_MAX_VALUE, sanitizeNumericInputValue } from '../../../../components/InputField/numericValidation';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../../../../components/InputField/testId';

export default function CurrencyField({
    id,
    className,
    label = "Amount",
    placeholder,
    value: initialValue,
    onChange,
    isDisabled,
    notifyParent,
    showErr,
    required,
    err,
    prevValue,
    onCommit,
    maxValue = DEFAULT_NUMERIC_MAX_VALUE,
    minValue,
}) {
    const [value, setValue] = useState(initialValue || '');
    const [error, setError] = useState(null);
    const [hasError, setHasError] = useState(false);
    const { t } = useTranslation()
    const fieldTestId = resolveAutomationTestId(id, label);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        setError(err);
        setHasError(showErr);
    }, [err, showErr]);

    const handleChange = (newValue) => {
        const nextValue = sanitizeNumericInputValue(newValue, {
            uiType: 'currencyField',
            label,
            maxValue,
            minValue,
            uiRegex: '^[0-9]+$',
        });

        if (!nextValue.isValid) {
            setError(nextValue.error);
            setHasError(true);
            notifyParent?.(false);
            return;
        }

        setValue(newValue);
        onChange(newValue);
        setError(null);
        setHasError(false);
    };

    const maskGenerator = createDefaultMaskGenerator('9999999999');

    return (
        <div className={`${className} ${(error && hasError) ? 'error' : ''}`} data-testid={fieldTestId}>
            <div className='inputfield__container'>
                <div className='inputfield__innercontainer_outline'>
                    <Label label={t(label)} required={required} />
                    <div className={`inputfield px-0`} style={{ margin: "0px 0px 5px 0px", height: "50px" }}>
                        <MaskedInput
                            id={id}
                            data-testid={buildAutomationChildTestId(fieldTestId, "input")}
                            {...INPUT_AUTOFILL_PROPS}
                            className="inputfield__input"
                            maskGenerator={maskGenerator}
                            value={value}
                            onChange={handleChange}
                            onBlur={() => onCommit?.(value)}
                            placeholder={placeholder ?? t("Amount")}
                            disabled={isDisabled}
                            style={{
                                height: "50px",
                                borderRadius: "15px",
                                paddingLeft: "15px",
                                border: "none",
                                fontSize: "14px",
                                outline: "none",
                                backgroundColor: prevValue?.has ? "#FFF9D6" : isDisabled ? "#F6F6F6" : "#F1FAFC",
                                cursor: isDisabled ? "not-allowed" : "default",
                                width: "100%",
                            }}
                        />
                    </div>
                </div>
                {/* Error marker inside inputfield__container so the V2 :has() rule can turn the
                    outline box red, consistent with every other field. */}
                {(error?.length && hasError) ? <em className="selectfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>{t(error)}</em> : null}
            </div>
            <PreviousValue
                show={prevValue?.has}
                value={prevValue?.value}
                testId={buildAutomationChildTestId(fieldTestId, "previous")}
            />
        </div>
    );
}

const Label = ({ label, required }) => {
    return (
        <label
            style={{
                minWidth: "120px",
                fontSize: 16,
                margin: "0px 0px 5px 5px",
                fontWeight: "500",
                textAlign: "left",
                display: 'block',
                marginBottom: '5px',
                color: "#495057"
            }}>
            {label ?? "Currency"}
            {required && <font color="red">*</font>}
        </label>
    );
};
